import styled from "@emotion/styled";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";

import { fadeInAndSlideUp } from "../../../styles/animations";

type DropdownMenuProps = {
  // The element that will trigger the dropdown, e.g. a button
  // The component must spread props and forward refs
  trigger: React.ReactNode;
  children: React.ReactNode; // the content of the dropdown menu, e.g. a list of items.

  // Trigger Props
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Content Props
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  width?: string | number; // Set to "var(--radix-dropdown-menu-trigger-width)" if you want it to match the trigger
  maxHeight?: string | number;
  maxWidth?: string | number;
  // Runs when the closing menu returns focus to the trigger; call event.preventDefault() to focus something else
  onCloseAutoFocus?: (event: Event) => void;
};

function DropdownMenu({
  trigger,
  children,
  // Trigger Props
  modal = true,
  open,
  onOpenChange,
  // Content Props
  align = "start",
  side = "bottom",
  width,
  maxHeight,
  maxWidth,
  onCloseAutoFocus,
}: DropdownMenuProps) {
  return (
    <DropdownMenuRoot onOpenChange={onOpenChange} open={open} modal={modal}>
      <DropdownMenuTrigger asChild aria-label="Dropdown Menu">
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={4}
        width={width}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

export default DropdownMenu;

const DropdownMenuRoot = styled(RadixDropdown.Root)({
  zIndex: 1000,
});

const DropdownMenuTrigger = RadixDropdown.Trigger;

// Keeps maxWidth off the DOM element; the other props are forwarded as before
const DropdownMenuContent = styled(RadixDropdown.Content, {
  shouldForwardProp: (prop) => prop !== "maxWidth",
})<{
  width?: number | string;
  maxHeight?: number | string;
  maxWidth?: number | string;
}>(({ width, maxHeight, maxWidth, theme }) => ({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  gap: 4,
  alignItems: "flex-start",
  backgroundColor: "white",
  borderRadius: 4,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.07)",
  padding: 4,
  width,
  boxSizing: "border-box",
  fontFamily: theme.font.fontFamily,
  animation: `${fadeInAndSlideUp} 0.3s ease-out`,
  zIndex: 1000,
  ...(maxHeight !== undefined && {
    maxHeight,
    overflowY: "scroll",
  }),
  ...(maxWidth !== undefined && { maxWidth }),
}));
